import {SelectionModel} from '@angular/cdk/collections';
import {FlatTreeControl} from '@angular/cdk/tree';
import {Component, Injectable, Input} from '@angular/core';
import {MatTreeFlatDataSource, MatTreeFlattener} from '@angular/material/tree';
import {BehaviorSubject} from 'rxjs';
import {ServiceDefService} from "../../../../services/servicedef.service";
import {Settings} from "../../../../services/settings";

export class RegionItemNode {
    children: RegionItemNode[];
    item: string;
}

/** Flat to-do item node with expandable and level information */
export class RegionItemFlatNode {
    item: string;
    level: number;
    expandable: boolean;
}
/**
 * Checklist database, it can build a tree structured Json object.
 * Each node in Json object represents a to-do item or a category.
 * If a node is a category, it has children items and new items can be added under the category.
 */
@Injectable()
export class ChecklistDatabase {

    public regions: any;

    dataChange = new BehaviorSubject<RegionItemNode[]>([]);
    get data(): RegionItemNode[] { return this.dataChange.value; }

    constructor(private serviceDefService: ServiceDefService) {
        this.queryRegions();
    }
    queryRegions(): void {

        this.serviceDefService.getAllRegions().subscribe(
            (result) => {
                this.regions = result;
                let JsonPayload = '{';
                let lastCounty = '';

                for(let rec of result) {

                    if(rec.county !== lastCounty) {
                        if(JsonPayload.length > 1) {
                            JsonPayload += '], ';
                        }
                        JsonPayload += '\"'+ rec.description + '\" : [';
                    }
                    else {
                        JsonPayload += ',';
                    }
                    JsonPayload += '\"'+ rec.zipCode + ' ('+ rec.county+ ')\"';
                    lastCounty = rec.county;
                }
                JsonPayload += ']}';

                try {
                    const JsonObject = JSON.parse( JsonPayload );
                    this.initialize(JsonObject);
                }
                catch(error) {
                    Settings.getInstance().handleError(error, 'Parsing JSON string: '+ JsonPayload);
                }
            },
            (error) => {
                Settings.getInstance().handleError(error, "Fetching regions");
            }
        );
    }
    initialize(jsonString: object) {
        // Build the tree nodes from Json object. The result is a list of `RegionItemNode` with nested
        //     file node as children.
        const data = this.buildFileTree(jsonString, 0);

        // Notify the change.
        this.dataChange.next(data);
    }

    /**
     * Build the file structure tree. The `value` is the Json object, or a sub-tree of a Json object.
     * The return value is the list of `RegionItemNode`.
     */
    buildFileTree(obj: object, level: number): RegionItemNode[] {

        return Object.keys(obj).reduce<RegionItemNode[]>((accumulator, key) => {
            const value = obj[key];
            const node = new RegionItemNode();
            node.item = key;

            if (value != null) {
                if (typeof value === 'object') {
                    node.children = this.buildFileTree(value, level + 1);
                } else {
                    node.item = value;
                }
            }

            return accumulator.concat(node);
        }, []);
    }
}

/**
 * @title Tree with checkboxes
 */
@Component({
    selector: 'app-regions',
    templateUrl: './regions.component.html',
    styleUrls: ['./regions.component.css'],
    providers: [ChecklistDatabase]
})
export class RegionsComponent {

    private _disabledFlag: boolean | null;
    private _serviceDefId: number | null;
    private records: any;
    private regions: any;
    private levels: any;

    /** Map from flat node to nested node. This helps us finding the nested node to be modified */
    public flatNodeMap = new Map<RegionItemFlatNode, RegionItemNode>();
    /** Map from nested node to flattened node. This helps us to keep the same object for selection */
    public nestedNodeMap = new Map<RegionItemNode, RegionItemFlatNode>();
    public treeControl: FlatTreeControl<RegionItemFlatNode>;
    public treeFlattener: MatTreeFlattener<RegionItemNode, RegionItemFlatNode>;
    public dataSource: MatTreeFlatDataSource<RegionItemNode, RegionItemFlatNode>;

    /** The selection for checklist */
    public checklistSelection = new SelectionModel<RegionItemFlatNode>(true /* multiple */);

    @Input() set disabledFlag (value: boolean) {
        this._disabledFlag = value;

        if(document.getElementById('regionDivId') === null ) {
            return;
        }

        if(this._disabledFlag === true) {
            document.getElementById('regionDivId').classList.add('regions-container-disabled');
        }
        else {
            document.getElementById('regionDivId').classList.remove('regions-container-disabled');
        }
    }

    public get disabledFlag(): boolean {
        return this._disabledFlag;
    }
    @Input() set serviceDefId (value: number) {
        this._serviceDefId = value;
    }

    constructor(private database: ChecklistDatabase, private serviceDefService:ServiceDefService) {
        this.treeFlattener = new MatTreeFlattener(this.transformer, this.getLevel,
            this.isExpandable, this.getChildren);
        this.treeControl = new FlatTreeControl<RegionItemFlatNode>(this.getLevel, this.isExpandable);
        this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

        database.dataChange.subscribe(data => {
            this.dataSource.data = data;
            this.regions = database.regions;
        });
    }

    public queryServiceRegions():void {
        this.records = [] ;

        if(this._serviceDefId !== null ){
            this.serviceDefService.getServiceRegionsByServiceDefId(this._serviceDefId).subscribe (
                (result) => {
                    this.records = result;
                    this.displaySelectedRegions();
                    },
                (error) => {
                    Settings.getInstance().handleError(error,"fetching Service Regions by service def ID");
                }
            )
        }
    }

    getLevel = (node: RegionItemFlatNode) => node.level;
    isExpandable = (node: RegionItemFlatNode) => node.expandable;
    getChildren = (node: RegionItemNode): RegionItemNode[] => node.children;
    hasChild = (_: number, _nodeData: RegionItemFlatNode) => _nodeData.expandable;
    hasNoContent = (_: number, _nodeData: RegionItemFlatNode) => _nodeData.item === '';

    /**
     * Transformer to convert nested node to flat node. Record the nodes in maps for later use.
     */
    transformer = (node: RegionItemNode, level: number) => {
        const existingNode = this.nestedNodeMap.get(node);
        const flatNode = existingNode && existingNode.item === node.item
            ? existingNode
            : new RegionItemFlatNode();
        flatNode.item = node.item;
        flatNode.level = level;
        flatNode.expandable = !!node.children;
        this.flatNodeMap.set(flatNode, node);
        this.nestedNodeMap.set(node, flatNode);
        return flatNode;
    }

    /** Whether all the descendants of the node are selected */
    descendantsAllSelected(node: RegionItemFlatNode): boolean {
        const descendants = this.treeControl.getDescendants(node);
        return descendants.every(child => this.checklistSelection.isSelected(child));
    }

    /** Whether part of the descendants are selected */
    descendantsPartiallySelected(node: RegionItemFlatNode): boolean {
        const descendants = this.treeControl.getDescendants(node);
        const result = descendants.some(child => this.checklistSelection.isSelected(child));
        return result && !this.descendantsAllSelected(node);
    }

    /** Toggle the to-do item selection. Select/deselect all the descendants node */
    regionItemSelectionToggle(node: RegionItemFlatNode): void {
        this.checklistSelection.toggle(node);
        const descendants = this.treeControl.getDescendants(node);
        this.checklistSelection.isSelected(node)
            ? this.checklistSelection.select(...descendants)
            : this.checklistSelection.deselect(...descendants);
    }
    public displaySelectedRegions(): void
    {
        for(let item of this.treeControl.dataNodes) {
            if (this.itemMatches(item) === true) {
                this.checklistSelection.select(item);
            } else {
                this.checklistSelection.deselect(item);
            }
        }
    }
    public itemMatches(node): boolean {

        if(node.level ===0) {
            return false;
        }
        const zip = node.item.substring(0,5);
        const county = node.item.substring(7, node.item.length -1);
        const regionId = this.regions.find(s => s.county === county && s.zipCode === zip).id;

        if(regionId === undefined) {
            return false;
        }

       // is it a selected item?  if so it should be in this.records...
       if(this.records.find(r => r.regionId === regionId) === undefined) {
           return false;
       }

       return true;
    }
    public submitSelectedRegions(): void {

        const Ids = new Array();
        const values = this.checklistSelection.selected;
        // for smoother operation, we will reload the records array with the updated
        // region entries so that we can display them after the update.
        this.records = new Array();

        for (let i = 0; i < values.length; i++) {
            if(values[i].level < 1) {
                continue;
            }
            const zip = values[i].item.substring(0,5);
            const county = values[i].item.substring(7,values[i].item.length -1);
            const region = this.regions.find(s => s.county === county && s.zipCode === zip);

            if(region === undefined) {
                continue;
            }
            this.records.push(new Object({id:0, regionId: region.id, serviceDefsId: this._serviceDefId}));
            Ids.push(region.id);
        }

        // update the service regions ...
        this.serviceDefService.updateServiceRegions(this._serviceDefId, Ids).subscribe(
            (result)=> {
                // nothing to do!
                this.displaySelectedRegions();
            },
            (error) => {
               Settings.getInstance().handleError(error, "updating Service regions")
            });
    }
}

